import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const adminApi = {
  // Authentication
  login: async (username, password) => {
    try {
      const response = await axios.post(`${API}/login`, { username, password });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Services Management
  getServices: async () => {
    try {
      const response = await axios.get(`${API}/services`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createService: async (serviceData) => {
    try {
      const response = await axios.post(`${API}/services`, serviceData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateService: async (serviceId, serviceData) => {
    try {
      const response = await axios.put(`${API}/services/${serviceId}`, serviceData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteService: async (serviceId) => {
    try {
      const response = await axios.delete(`${API}/services/${serviceId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Projects Management
  getProjects: async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createProject: async (projectData) => {
    try {
      const response = await axios.post(`${API}/projects`, projectData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateProject: async (projectId, projectData) => {
    try {
      const response = await axios.put(`${API}/projects/${projectId}`, projectData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteProject: async (projectId) => {
    try {
      const response = await axios.delete(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadProjectImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/projects/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Company Info Management
  getCompanyInfo: async () => {
    try {
      const response = await axios.get(`${API}/company`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateCompanyInfo: async (companyData) => {
    try {
      const response = await axios.put(`${API}/company`, companyData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Activities/News Management
  getActivities: async () => {
    try {
      const response = await axios.get(`${API}/activities`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createActivity: async (activityData) => {
    try {
      const response = await axios.post(`${API}/activities`, activityData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateActivity: async (activityId, activityData) => {
    try {
      const response = await axios.put(`${API}/activities/${activityId}`, activityData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteActivity: async (activityId) => {
    try {
      const response = await axios.delete(`${API}/activities/${activityId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadActivityImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/activities/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Customers Management
  getCustomers: async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createCustomer: async (customerData) => {
    try {
      const response = await axios.post(`${API}/customers`, customerData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateCustomer: async (customerId, customerData) => {
    try {
      const response = await axios.put(`${API}/customers/${customerId}`, customerData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteCustomer: async (customerId) => {
    try {
      const response = await axios.delete(`${API}/customers/${customerId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadCustomerLogo: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/customers/upload-logo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Partners Management
  getPartners: async () => {
    try {
      const response = await axios.get(`${API}/partners`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createPartner: async (partnerData) => {
    try {
      const response = await axios.post(`${API}/partners`, partnerData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updatePartner: async (partnerId, partnerData) => {
    try {
      const response = await axios.put(`${API}/partners/${partnerId}`, partnerData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deletePartner: async (partnerId) => {
    try {
      const response = await axios.delete(`${API}/partners/${partnerId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadPartnerLogo: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/partners/upload-logo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};
